import { Transition } from '@headlessui/react'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { Button } from '../Button'
import { Icon } from '../Icon'
import { Spinner } from '../Spinner'
import { tm } from '../tm'
import type {
  CSSProperties,
  ChangeEventHandler,
  FocusEventHandler,
  MouseEventHandler,
  ReactNode,
  WheelEvent,
} from 'react'
import type { FieldMetaState } from 'react-final-form'

export interface InputProps {
  autoFocus?: boolean
  blur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  change?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  clear?: MouseEventHandler<HTMLButtonElement>
  className?: string
  disabled?: boolean
  focus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  formMeta?: FieldMetaState<string | number>
  id?: string
  label?: string
  labelClassName?: string
  suffixClassName?: string
  hint?: string | ReactNode
  name?: string
  placeholder?: string
  required?: boolean
  resize?: boolean
  rows?: number
  showSpinner?: boolean
  spinnerStyle?: CSSProperties
  style?: CSSProperties
  computeFieldError?: (formMeta: FieldMetaState<string | number>) => boolean
  suffix?: string
  type?: string
  value: string | number
  showFormErrorLabel?: boolean
  autoComplete?: string
  inputMode?:
    | 'search'
    | 'text'
    | 'email'
    | 'tel'
    | 'none'
    | 'url'
    | 'numeric'
    | 'decimal'
}

const emptyObject = {}
const noop = () => {}
export function TextInputWithRef(
  {
    autoFocus = false,
    blur = noop,
    change = noop,
    clear = noop,
    className = '',
    labelClassName = '',
    suffixClassName = '',
    disabled = false,
    focus = noop,
    formMeta = emptyObject,
    id = undefined,
    name = undefined,
    label = '',
    placeholder = '',
    hint = undefined,
    required = false,
    resize = false,
    rows = 20,
    style = emptyObject,
    showSpinner = false,
    spinnerStyle = undefined,
    computeFieldError = undefined,
    suffix = undefined,
    type = 'text',
    showFormErrorLabel = true,
    autoComplete = 'on',
    value,
    ...props
  }: InputProps,
  ref: any,
) {
  const suffixRef = useRef<HTMLElement | null>(null)
  const [suffixWidth, setSuffixWidth] = useState<number | undefined>(undefined)
  // TODO: create a useError that will handle the error label by itself
  const [isErrorLabelVisible, showErrorLabel] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const [shouldShowPassword, setShouldShowPassword] = useState<boolean>(false)

  const hasError = computeFieldError
    ? computeFieldError(formMeta)
    : !!formMeta.error && !!formMeta.touched

  const onWheel =
    type === 'number'
      ? (e: WheelEvent<HTMLInputElement> & WheelEvent<HTMLTextAreaElement>) =>
          e?.currentTarget?.blur()
      : undefined

  const showPassword = () => {
    setShouldShowPassword(data => !data)
  }

  useEffect(() => {
    if (suffix && suffixRef.current) {
      const { width } = suffixRef.current.getBoundingClientRect()

      setSuffixWidth(width)
    }
  }, [suffix])

  useEffect(() => {
    if (!showFormErrorLabel) {
      return
    }
    if (hasError) {
      setError(formMeta.error)
      showErrorLabel(true)
    }
    if (!hasError && isErrorLabelVisible) {
      showErrorLabel(false)
      setTimeout(() => setError(undefined), 350)
    }
    if (hasError && isErrorLabelVisible && !error) {
      setTimeout(() => setError(formMeta.error), 0)
    }
  }, [hasError, error, formMeta.error, isErrorLabelVisible, showFormErrorLabel])

  const Component = resize ? 'textarea' : 'input'
  const isSearchInput = type === 'search'
  const isPasswordInput = type === 'password'

  const inpuType = isPasswordInput && shouldShowPassword ? 'input' : type

  return (
    <label
      ref={ref}
      htmlFor={id}
      className={tm(
        'relative flex w-full flex-col text-base transition-spacing delay-100 duration-300 ease-out',
        showFormErrorLabel && hasError && '!mb-10 delay-0 md:!mb-6',
        labelClassName,
      )}
    >
      {label ? (
        <span className="mb-2 block">
          {label}
          {required ? (
            <Icon
              icon="asterisk"
              inline
              size="tiny"
              className="align-text-top"
            />
          ) : null}
        </span>
      ) : null}
      {hint ? (
        <div className="mb-3 mt-1 text-xs font-normal">{hint}</div>
      ) : null}

      <div className="relative">
        {isSearchInput ? (
          <Icon
            icon="search"
            size="small"
            className="absolute left-3 top-1/2 z-[3] -translate-y-1/2"
          />
        ) : null}
        <Component
          id={id}
          type={inpuType}
          name={name}
          className={tm(
            'relative z-[2] h-12 w-full rounded-lg border border-inside bg-white p-3 text-sm text-black transition-input duration-300 ease-out hover:border-gray hover:shadow-input focus-visible:border-black focus-visible:shadow-input',
            resize && 'h-20 min-h-[80px] align-top',
            hasError &&
              'border-dark-orange bg-light-orange text-dark-orange hover:border-dark-orange hover:!shadow-none focus-visible:border-dark-orange focus-visible:!shadow-none',
            isSearchInput && 'pl-9 pr-8',
            isPasswordInput && 'pr-8',
            disabled &&
              'cursor-not-allowed border-dark-gray bg-light-gray text-dark-gray hover:border-dark-gray hover:!shadow-none focus:border-dark-gray focus:!shadow-none',
            className,
          )}
          rows={resize ? rows : undefined}
          placeholder={placeholder}
          style={{
            ...(suffixWidth
              ? {
                  paddingRight: `${
                    suffixWidth + 22 + (showSpinner ? 24 : 0)
                  }px`,
                }
              : {}),
            ...style,
          }}
          required={required}
          onBlur={blur}
          onChange={change}
          onFocus={focus}
          onWheel={onWheel}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          {...props}
        />

        {suffix ? (
          <span
            ref={suffixRef}
            className={tm(
              'pointer-events-none absolute right-2 top-[50%] z-[3] -translate-y-[50%] translate-x-0 transform-gpu text-sm text-dark-gray opacity-0 transition-transform delay-200 duration-200 ease-out',
              suffixWidth && 'opacity-1 -translate-x-2',
              suffixWidth && showSpinner && 'opacity-1 -translate-x-9',
              suffixClassName,
            )}
          >
            {suffix}
          </span>
        ) : null}

        {isSearchInput && value && !showSpinner ? (
          <Button
            variant="raw"
            onClick={clear}
            className="absolute right-3 top-1/2 z-[3] -translate-y-1/2 p-0"
          >
            <Icon icon="cross" size="medium" />
          </Button>
        ) : null}

        {isPasswordInput && !showSpinner ? (
          <Button
            variant="raw"
            onClick={showPassword}
            className="absolute right-3 top-1/2 z-[3] -translate-y-1/2 p-0"
          >
            <Icon icon={shouldShowPassword ? 'no-eye' : 'eye'} size="medium" />
          </Button>
        ) : null}

        {showSpinner ? (
          <Spinner
            className="absolute right-2 top-3 z-[3]"
            light
            style={spinnerStyle}
          />
        ) : null}
      </div>
      {showFormErrorLabel ? (
        <Transition
          show={isErrorLabelVisible}
          as="span"
          className="pointer-events-none absolute -bottom-6 left-0 z-[1] flex h-auto w-full transform-gpu items-center whitespace-nowrap text-xs text-dark-orange transition-transform duration-200 ease-out data-[closed]:-translate-y-2 data-[closed]:opacity-0"
        >
          <Icon icon="warning-round" size="small" className="mr-1" /> {error}
        </Transition>
      ) : null}
    </label>
  )
}
export const TextInput = forwardRef(TextInputWithRef)
